import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useState
} from 'react';

import { Stack } from '@mui/material';

import { EditableItemCard } from '@/shared/ui/components/card/EditableItemCard';

interface IExpandableCardProps {
  children: ReactNode;
  onChangeClick?: () => void;
  onDeleteClick?: () => void;
  isDisabled?: boolean;
  textRefs: RefObject<HTMLDivElement>[];
}

interface IExpandableCardContext {
  maxLines: number;
}

const ExpandableCardContext = createContext<IExpandableCardContext>({
  maxLines: 2
});

/**
 * Хук для получения maxLines из ExpandableCard
 */
export const useExpandableCard = (): IExpandableCardContext => {
  return useContext(ExpandableCardContext);
};

/**
 * Обертка для карточек с раскрывающимся контентом
 * @param props - пропсы
 * @param props.children - контент карточки
 * @param props.onDeleteClick - событие удаления
 * @param props.isDisabled - заблокирована ли карточка
 * @param props.onChangeClick - событие редактирования
 * @param props.textRefs - массив ref-ов на текстовые элементы для проверки переполнения
 */
export const ExpandableCard = ({
  children,
  onDeleteClick,
  isDisabled,
  onChangeClick,
  textRefs
}: IExpandableCardProps) => {
  const [isOpenCard, setIsOpenCard] = useState(false);
  const [isTouchable, setIsTouchable] = useState(false);

  const maxLines = !isTouchable || isOpenCard ? 20 : 2;

  useEffect(() => {
    const hasOverflow = textRefs.some(ref => {
      const lines = checkTextLines(ref.current);
      return lines > 2;
    });
    if (hasOverflow) {
      setIsTouchable(true);
    }
  }, [textRefs]);

  /**
   * Проверяет, сколько строк занимает текст в элементе
   * @param ref - ссылка на HTMLDivElement
   */
  const checkTextLines = (ref?: HTMLDivElement | null): number => {
    if (!ref) {
      return 0;
    } else {
      return ref.getBoundingClientRect().height / 16;
    }
  };

  return (
    <EditableItemCard
      onChangeClick={onChangeClick}
      onDeleteClick={onDeleteClick}
      isDisabled={isDisabled}
    >
      <Stack
        gap={2}
        onClick={() => {
          if (isTouchable) setIsOpenCard(!isOpenCard);
        }}
        sx={{
          cursor: isTouchable ? 'pointer' : 'auto',
          overflow: 'hidden',
          transition: 'max-height 0.8s ease',
          maxHeight: isOpenCard ? 1000 : 150,
          pr: 4
        }}
      >
        <ExpandableCardContext.Provider value={{ maxLines }}>
          {children}
        </ExpandableCardContext.Provider>
      </Stack>
    </EditableItemCard>
  );
};
